import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

interface CarSelectorProps {
  value: string;                 // current car_details text
  onChange: (val: string) => void; // callback with formatted "CarName — Variant (Color)"
}

const CarSelector: React.FC<CarSelectorProps> = ({ value, onChange }) => {
  const [cars, setCars] = useState<any[]>([]);
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [useManual, setUseManual] = useState(false);
  const [manualText, setManualText] = useState(value || '');

  useEffect(() => {
    apiService.getCrmCars().then((res: any) => {
      if (res.success) setCars(res.data);
    }).catch(() => {});
  }, []);

  // When dropdowns change, build the car_details string
  useEffect(() => {
    if (useManual) return;
    if (!selectedCar) { onChange(''); return; }
    const car = cars.find(c => c.id === selectedCar);
    if (!car) return;
    let text = car.name;
    if (selectedVariant) text += ` — ${selectedVariant}`;
    if (selectedColor) text += ` (${selectedColor})`;
    onChange(text);
  }, [selectedCar, selectedVariant, selectedColor, useManual]);

  const currentCar = cars.find(c => c.id === selectedCar);
  const variants = currentCar?.variants || [];
  const currentVariant = variants.find((v: any) => v.name === selectedVariant);
  const colors = currentVariant?.colors || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <label style={{ fontWeight: 600, fontSize: '13px' }}>🚗 Car Details</label>
        <button
          type="button"
          onClick={() => { setUseManual(!useManual); if (!useManual) onChange(manualText); }}
          style={{
            fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
            background: useManual ? 'var(--crm-primary-light)' : 'var(--crm-bg)',
            color: useManual ? 'var(--crm-primary)' : 'var(--crm-text-muted)',
            border: `1px solid ${useManual ? 'var(--crm-primary)' : 'var(--crm-border)'}`,
            fontWeight: 600
          }}
        >
          {useManual ? '← Use Dropdown' : '✏ Type Manually'}
        </button>
      </div>

      {useManual ? (
        <input
          type="text"
          placeholder="e.g. Maruti Swift ZXI 2024"
          value={manualText}
          onChange={e => { setManualText(e.target.value); onChange(e.target.value); }}
          style={{ width: '100%', padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
        />
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Car Model */}
          <select
            value={selectedCar}
            onChange={e => { setSelectedCar(e.target.value); setSelectedVariant(''); setSelectedColor(''); }}
            style={{ flex: '1 1 160px', padding: '8px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
          >
            <option value="">— Select Car —</option>
            {cars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {/* Variant */}
          {selectedCar && variants.length > 0 && (
            <select
              value={selectedVariant}
              onChange={e => { setSelectedVariant(e.target.value); setSelectedColor(''); }}
              style={{ flex: '1 1 140px', padding: '8px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="">— Select Variant —</option>
              {variants.map((v: any) => <option key={v.name} value={v.name}>{v.name}</option>)}
            </select>
          )}

          {/* Color */}
          {selectedVariant && colors.length > 0 && (
            <select
              value={selectedColor}
              onChange={e => setSelectedColor(e.target.value)}
              style={{ flex: '1 1 120px', padding: '8px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="">— Color —</option>
              {colors.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Preview */}
      {value && !useManual && (
        <div style={{ marginTop: 6, fontSize: '12px', color: 'var(--crm-success)', fontWeight: 600 }}>
          ✅ {value}
        </div>
      )}
    </div>
  );
};

export default CarSelector;
